/**
 * Grant of Representation Upload Dialog
 *
 * Custom dialog for uploading Grant of Representation documents with
 * state-specific fields for QLD and VIC matters.
 *
 * QLD: Requires Probate Access Code (grantOfRepresentationCode)
 * VIC: Requires Unique Identifier Code (grantOfRepresentationCode) and
 *      Application Number (grantOfRepresentationReference)
 */

'use client';

import { useState } from 'react';
import { Award } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DropZone } from '../upload/drop-zone';
import { FilePreviewWithOptions } from '../upload/file-preview-with-options';
import { useDocumentUpload } from '@/hooks/use-document-upload';
import type { CoreDocumentUploadHandler } from './core-document-upload-handler';
import { useMatter, useUpdateMatter } from '@/lib/services/matters/hooks';
import { matterKeys } from '@/lib/query-keys';
import { useQueryClient } from '@tanstack/react-query';
import { FileTypeSlug } from '@/lib/services/files/file-types';
import { toast } from '@/lib/toast';
import { acceptedExtensionsFor } from '@/lib/constants/file-type-accept';

interface GrantOfRepresentationUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId?: number; // Optional - if provided, shows state-specific fields for QLD/VIC
  onUpload: CoreDocumentUploadHandler;
}

type MatterState = 'QLD' | 'VIC' | 'OTHER';

function getMatterState(state?: string): MatterState {
  if (!state) return 'OTHER';
  const normalizedState = state.toUpperCase().trim();
  if (normalizedState === 'QLD' || normalizedState === 'QUEENSLAND') {
    return 'QLD';
  }
  if (normalizedState === 'VIC' || normalizedState === 'VICTORIA') {
    return 'VIC';
  }
  return 'OTHER';
}

export function GrantOfRepresentationUploadDialog({
  open,
  onOpenChange,
  matterId,
  onUpload
}: GrantOfRepresentationUploadDialogProps) {
  const queryClient = useQueryClient();

  // Form state for grant codes
  const [grantCode, setGrantCode] = useState('');
  const [grantReference, setGrantReference] = useState('');

  // Fetch matter to determine state (only if matterId is provided)
  const { data: matter, isLoading: isMatterLoading } = useMatter(matterId ?? 0);
  const updateMatterMutation = useUpdateMatter({ showToast: false });

  // Only show grant fields if matterId is provided and matter is QLD or VIC
  const hasMatterId = matterId !== undefined && matterId > 0;
  const matterState = hasMatterId
    ? getMatterState(matter?.decedentAddress?.state)
    : 'OTHER';
  const isQLD = matterState === 'QLD';
  const isVIC = matterState === 'VIC';
  const showGrantFields = hasMatterId && (isQLD || isVIC);

  // Prefill grant codes from matter during render whenever the open/matter
  // identity changes.
  const prefillKey = open && matter ? `${matter.id}` : null;
  const [prevPrefillKey, setPrevPrefillKey] = useState<string | null>(
    prefillKey
  );
  if (prefillKey && prefillKey !== prevPrefillKey) {
    setPrevPrefillKey(prefillKey);
    setGrantCode(matter?.grantOfRepresentationCode ?? '');
    setGrantReference(matter?.grantOfRepresentationReference ?? '');
  }
  if (!prefillKey && prevPrefillKey !== null) {
    setPrevPrefillKey(null);
  }

  // Upload hook configuration
  const {
    files,
    hasFiles,
    isUploading,
    canUpload,
    removeFile,
    updateFileType,
    updateCertifiedCopy,
    handleFilesSelected,
    handleUpload: baseHandleUpload,
    handleClose: baseHandleClose,
    openFileDialog,
    getInputProps
  } = useDocumentUpload(
    {
      fileType: FileTypeSlug.GRANT_OF_representation,
      maxFiles: 1
    },
    {
      onUpload: async (uploadFiles, helpers, extract) => {
        // First upload the file
        await onUpload(uploadFiles, helpers, extract);

        // Then update the matter with grant codes if provided
        if (hasMatterId && showGrantFields && (grantCode || grantReference)) {
          try {
            await updateMatterMutation.mutateAsync({
              matterId: matterId!,
              data: {
                ...(grantCode && { grantOfRepresentationCode: grantCode }),
                ...(grantReference && {
                  grantOfRepresentationReference: grantReference
                })
              }
            });
            // Invalidate matter cache to refresh the document card
            await queryClient.invalidateQueries({
              queryKey: matterKeys.detail(matterId!)
            });
          } catch (error) {
            console.error('Failed to update matter with grant codes:', error);
            toast.error('File uploaded but failed to save grant codes');
          }
        }
      },
      onClose: () => {
        onOpenChange(false);
        // Reset form state
        setGrantCode('');
        setGrantReference('');
      }
    }
  );

  const handleClose = () => {
    if (!isUploading) {
      baseHandleClose();
      setGrantCode('');
      setGrantReference('');
    }
  };

  const handleDialogOpenChange = (dialogOpen: boolean) => {
    if (dialogOpen) {
      onOpenChange(dialogOpen);
    } else {
      handleClose();
    }
  };

  // Determine if form is valid for upload
  const isFormValid = () => {
    if (!hasFiles) return false;
    // Grant codes are optional - user can add them later via the document card
    return true;
  };

  const acceptedFileTypes = acceptedExtensionsFor('grant-of-representation');

  // Get label text based on state
  const getCodeLabel = () => {
    if (isQLD) return 'Probate Access Code';
    if (isVIC) return 'Unique Identifier Code';
    return 'Grant Code';
  };

  const getCodePlaceholder = () => {
    if (isQLD) return 'Enter the QLD Probate Access Code';
    if (isVIC) return 'Enter the VIC Unique Identifier Code';
    return 'Enter the grant code';
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Upload Grant of Representation</DialogTitle>
          <DialogDescription>
            Please upload the Grant of Probate or Letters of Administration
            issued by the court. This document provides legal authority to
            administer the estate.
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <Award className="h-4 w-4" />
          <AlertDescription>
            <strong>Important:</strong> The Grant of Representation is the
            court's official authorization to manage the deceased's estate.
            Ensure you upload the complete document with all pages and the court
            seal visible.
          </AlertDescription>
        </Alert>

        <div className="min-w-0 space-y-4">
          {/* State-specific form fields */}
          {showGrantFields && !isMatterLoading && (
            <div className="space-y-4 rounded-lg border p-4">
              <div className="space-y-2">
                <Label htmlFor="grant-code">{getCodeLabel()}</Label>
                <Input
                  id="grant-code"
                  value={grantCode}
                  onChange={(e) => setGrantCode(e.target.value)}
                  placeholder={getCodePlaceholder()}
                  disabled={isUploading}
                />
              </div>

              {(isQLD || isVIC) && (
                <div className="space-y-2">
                  <Label htmlFor="grant-reference">
                    {isQLD ? 'Reference Number' : 'Application Number'}
                  </Label>
                  <Input
                    id="grant-reference"
                    value={grantReference}
                    onChange={(e) => setGrantReference(e.target.value)}
                    placeholder={
                      isQLD
                        ? 'Enter the Reference Number'
                        : 'Enter the VIC Application Number'
                    }
                    disabled={isUploading}
                  />
                </div>
              )}
            </div>
          )}

          {/* File upload area */}
          {!hasFiles && (
            <DropZone
              onDrop={handleFilesSelected}
              onClick={openFileDialog}
              disabled={isUploading}
              acceptedFileTypes={acceptedFileTypes}
            >
              <Award className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
              <p className="mb-2 text-sm font-medium">
                Drop Grant of Representation here
              </p>
              <p className="text-muted-foreground text-xs">
                or click to browse for the grant document
              </p>
              <p className="text-muted-foreground mt-2 text-xs">
                Accepted formats: PDF, JPG, PNG
              </p>
            </DropZone>
          )}

          {hasFiles && (
            <ScrollArea className="h-[200px] pr-4">
              <div className="space-y-3">
                {files.map((file) => (
                  <FilePreviewWithOptions
                    key={file.id}
                    file={file}
                    onRemove={removeFile}
                    onFileTypeChange={updateFileType}
                    onCertifiedCopyChange={updateCertifiedCopy}
                    disabled={isUploading}
                    hideFileTypeSelector={true}
                    excludeCrucial={true}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {hasFiles && (
          <p className="text-muted-foreground text-xs">
            Extracting reads this document and proposes contacts, assets and
            beneficiaries for you to review. You can also run it later from the
            Documents page.
          </p>
        )}

        <DialogFooter>
          <Button
            size="xs"
            density="compact"
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          {!hasFiles && (
            <Button size="xs" density="compact" onClick={openFileDialog}>
              Browse Files
            </Button>
          )}
          {hasFiles && (
            <Button
              size="xs"
              density="compact"
              variant="outline"
              onClick={() => baseHandleUpload(false)}
              disabled={!isFormValid() || isUploading || !canUpload}
            >
              {isUploading ? 'Uploading...' : 'Upload Grant'}
            </Button>
          )}
          {hasFiles && (
            <Button
              size="xs"
              density="compact"
              onClick={() => baseHandleUpload(true)}
              disabled={!isFormValid() || isUploading || !canUpload}
            >
              {isUploading ? 'Uploading...' : 'Upload & extract'}
            </Button>
          )}
        </DialogFooter>

        <input {...getInputProps()} />
      </DialogContent>
    </Dialog>
  );
}
